/**
 * WVB-02 — Event Financial Budget Workspace (ADR-B10)
 * Route: /crm/events/:eid/financials
 *
 * Implements:
 *   Tab 1 — Overview:      8-KPI panel, budget lifecycle actions (ADR-B12)
 *   Tab 2 — Budget Lines:  Editable DRAFT line-item table with Decimal arithmetic
 *   Tab 3 — Versions:      Read-only version history with detail drill-down (ADR-B11)
 *
 * ADR-B12: B2 supports DRAFT → SUBMITTED only. APPROVED is B3 scope.
 * Shared data-loading: financials + active budget + lines fetched once, passed down.
 * Refresh cascades across all three tabs without duplicate API calls.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, TrendingUp, Loader2, AlertCircle,
  LayoutDashboard, List, History, Shield, BarChart2, Activity,
} from "lucide-react";
import { budgetApi, hasCapability } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import EventFinancialsOverviewTab from "./EventFinancialsOverviewTab";
import EventBudgetLinesTab from "./EventBudgetLinesTab";
import EventBudgetVersionsTab from "./EventBudgetVersionsTab";
import EventBudgetApprovalsTab from "./EventBudgetApprovalsTab";
import FinancialContextWidget from "./FinancialContextWidget";
import EventFinancialActualsTab from "./EventFinancialActualsTab";

const FIN_TABS = [
  { id: "overview",     label: "Overview",          icon: LayoutDashboard },
  { id: "budget_lines", label: "Budget Lines",      icon: List },
  { id: "versions",     label: "Versions",          icon: History },
  { id: "approvals",    label: "Approvals",         icon: Shield },
  // WVB-04: Financial Context tab — revenue vs. budget, margin health
  { id: "context",      label: "Financial Context", icon: BarChart2, cap: "financial.dashboard.view" },
  // EFIN-03: AR & AP Actuals — live operational financial 360°
  { id: "actuals",      label: "AR & AP Actuals",   icon: Activity,  cap: "event.finance.view" },
];

export default function EventFinancialsWorkspace() {
  const { eid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");

  const canView = hasCapability(user, "budget.view");

  // Shared data state — passed to child tabs to avoid duplicate requests
  const [financials, setFinancials]   = useState(null);
  const [budget,     setBudget]       = useState(null);
  const [linesData,  setLinesData]    = useState(null);
  const [loading,    setLoading]      = useState(true);
  const [error,      setError]        = useState(null);

  const loadAll = useCallback(async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      // KPI overview (always available)
      const finRes = await budgetApi.getFinancials(eid);
      setFinancials(finRes.data);

      // Active budget + lines (404 is OK — no budget yet)
      try {
        const bRes = await budgetApi.getBudget(eid);
        setBudget(bRes.data);
        if (bRes.data?.id) {
          const lRes = await budgetApi.getLines(eid, bRes.data.id);
          setLinesData(lRes.data);
        } else {
          setLinesData(null);
        }
      } catch (budgetErr) {
        if (budgetErr.response?.status !== 404) {
          // Unexpected error — log but don't block the page
          console.warn("Budget load error (non-404):", budgetErr);
        }
        setBudget(null);
        setLinesData(null);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load financial data");
    } finally {
      setLoading(false);
    }
  }, [eid, canView]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Authorization guard ──────────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 px-6">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p>You do not have access to Event Financials.</p>
          <p className="text-xs text-slate-400 mt-1">Requires: budget.view</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="financials-workspace">

      {/* ── Workspace header ────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4">

          {/* Breadcrumb + title */}
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate(`/crm/events/${eid}`)}
              className="text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="fin-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <TrendingUp className="w-5 h-5 text-indigo-500" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">Event Financials</h1>
              <p className="text-xs text-slate-400">Internal cost budget · Wave B2</p>
            </div>
            {financials?.budget_number && (
              <div className="hidden sm:flex items-center gap-1.5 ml-2 text-xs text-slate-400">
                <span className="text-slate-300">·</span>
                <span className="font-medium text-slate-600">{financials.budget_number}</span>
                <span>v{financials.budget_version}</span>
              </div>
            )}
          </div>

          {/* Tab navigation */}
          <div className="flex gap-1 overflow-x-auto pb-0.5">
            {FIN_TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              // Hide context tab if user lacks the capability
              if (t.cap && !hasCapability(user, t.cap)) return null;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`fin-tab-${t.id}`}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg
                    whitespace-nowrap transition-all
                    ${active
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                    }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Tab body ────────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="financials-loading">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-red-600 justify-center py-16" data-testid="financials-error">
            <AlertCircle className="w-5 h-5" /> {error}
          </div>
        ) : (
          <>
            {tab === "overview" && (
              <EventFinancialsOverviewTab
                eid={eid}
                financials={financials}
                onRefresh={loadAll}
              />
            )}

            {tab === "budget_lines" && (
              <EventBudgetLinesTab
                eid={eid}
                budget={budget}
                linesData={linesData}
                onRefresh={loadAll}
              />
            )}

            {tab === "versions" && (
              <EventBudgetVersionsTab
                eid={eid}
                currentBudgetId={budget?.id}
              />
            )}

            {tab === "approvals" && (
              <EventBudgetApprovalsTab
                eid={eid}
                budget={budget}
                onRefresh={loadAll}
              />
            )}

            {/* WVB-04: Financial Context — revenue vs. approved budget, margin health */}
            {tab === "context" && (
              <FinancialContextWidget eid={eid} />
            )}

            {/* EFIN-03: AR & AP Actuals — live event financial 360° */}
            {tab === "actuals" && (
              <EventFinancialActualsTab
                eventId={eid}
                eventName={financials?.event_name || ""}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
